import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScwMatInputRule, ScwMatInputSize, ScwMatInputType } from './scw-mat-input.model';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { IotDeviceManagementPortalAppState } from '../../../../store/iotDeviceManagementPortalApp.reducer';
import * as _ from 'lodash';
import { isEmail } from 'class-validator';

@Component({
  selector: 'scw-mat-input',
  templateUrl: './scw-mat-input.component.html',
  styleUrls: ['./scw-mat-input.component.scss'],
  host: { class: 'scw-mat-input-host' },
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScwMatInputComponent),
      multi: true,
    },
  ],
})
export class ScwMatInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() inputModel;
  @Input() isValid: boolean = false;
  @Input() label: string = null;
  @Input() type: ScwMatInputType = 'text';
  @Input() size: ScwMatInputSize = 'md';
  @Input() placeholder: string = null;
  @Input() hint: string = null;
  @Input() maxlength: string = null;
  @Input() disabled: boolean = false;
  @Input() hideEyeIcon: boolean = false;
  @Input() block: boolean = false;
  @Input() className:
    | string[]
    | {
        [klass: string]: any;
      };
  @Input() hasErrors: boolean = false;
  @Input() errorText: string;
  @Input() rules: ScwMatInputRule[] = [];
  @Input() noPadding: boolean = false;
  @Input() dataPropertyKey?: string;

  @Output() inputModelChange: EventEmitter<string | number> = new EventEmitter<string | number>();
  @Output() isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onKeyup: EventEmitter<string | number> = new EventEmitter<string | number>();
  @Output() onKeyupEnter: EventEmitter<string | number> = new EventEmitter<string | number>();

  public manageableType: ScwMatInputType = 'text';
  private isAnyError: boolean = false;
  private decimalSeparator: '.' | ',' = '.';
  private readonly integerRegex: RegExp = new RegExp(/^[-+]?[0-9]\d*$/);
  private readonly subscriptions: Subscription[] = [];
  private onChange: any = () => {};
  private onTouch: any = () => {};

  constructor(private readonly store: Store<IotDeviceManagementPortalAppState>) {}

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(input: string): void {
    this.inputModel = input;
  }

  public reset(): void {
    if (this.type === 'password') {
      this.manageableType = 'password';
    }

    this.inputModel = '';
    this.clearErrorMessage();
  }

  private isValidEqualizer(isValid: boolean): void {
    this.isValid = isValid;
    this.isValidChange.emit(this.isValid);
  }

  private showErrorMessage(message: string): void {
    this.isValidEqualizer(false);
    this.isAnyError = true;
    this.hasErrors = true;
    this.errorText = message ? message : '';
  }

  public clearErrorMessage(): void {
    this.isValidEqualizer(true);
    this.isAnyError = false;
    this.hasErrors = false;
    this.errorText = null;
  }

  private requiredRule(rule: ScwMatInputRule): void {
    if (_.isNil(this.inputModel) || this.inputModel.length === 0) {
      this.showErrorMessage(rule.message ?? ('scwMatForm.validation.required'));
    }
  }

  private minValueRule(rule: ScwMatInputRule): void {
    if (this.inputModel && (!this.integerRegex.test(this.inputModel) || this.inputModel < rule.minValue)) {
      this.showErrorMessage(
        rule.message ?? ('scwMatForm.validation.minValue'),
      );
    }
  }

  private maxValueRule(rule: ScwMatInputRule): void {
    if (this.inputModel && (!this.integerRegex.test(this.inputModel) || this.inputModel > rule.maxValue)) {
      this.showErrorMessage(
        rule.message ?? ('scwMatForm.validation.maxValue'),
      );
    }
  }

  private minLengthRule(rule: ScwMatInputRule): void {
    if (this.inputModel && this.inputModel.length < rule.minLength) {
      this.showErrorMessage(
        rule.message ?? ('scwMatForm.validation.minLength'),
      );
    }
  }

  private maxLengthRule(rule: ScwMatInputRule): void {
    if (this.inputModel && this.inputModel.length > rule.maxLength) {
      this.showErrorMessage(
        rule.message ?? ('scwMatForm.validation.maxLength'),
      );
    }
  }

  private decimalRule(rule: ScwMatInputRule): void {
    if (!this.inputModel) {
      return;
    }

    const separator = rule.decimal.separator || this.decimalSeparator;
    const min = rule.decimal.min || 0;
    const max = rule.decimal.max;
    const integerStepMin = rule.decimal.integerStep.min || 0;
    const integerStepMax = rule.decimal.integerStep.max;
    const decimalStepMin = rule.decimal.decimalStep.min || 0;
    const decimalStepMax = rule.decimal.decimalStep.max;
    const decimalRegex: RegExp =
      separator === '.'
        ? new RegExp(
            // tslint:disable-next-line:max-line-length
            `^[-]?[0-9]{${integerStepMin},${integerStepMax}}(\\.[0-9]{${decimalStepMin},${decimalStepMax}})?$`,
          )
        : new RegExp(
            // tslint:disable-next-line:max-line-length
            `^[-]?[0-9]{${integerStepMin},${integerStepMax}}(,[0-9]{${decimalStepMin},${decimalStepMax}})?$`,
          );
    const inputModelClone: string = String(_.cloneDeep(this.inputModel));
    const actualValue: number = separator === '.' ? this.inputModel : Number(inputModelClone.replace(',', '.'));

    if (!decimalRegex.test(this.inputModel) || actualValue < min || actualValue > max) {
      this.showErrorMessage(
        rule.message ??
          ('scwMatForm.validation.decimal'),
      );
    }
  }

  private rangeValueRule(rule: ScwMatInputRule): void {
    if (
      this.inputModel &&
      (!this.integerRegex.test(this.inputModel) ||
        this.inputModel < rule.rangeValue.min ||
        this.inputModel > rule.rangeValue.max)
    ) {
      this.showErrorMessage(
        rule.message ??
          ('scwMatForm.validation.rangeValue'),
      );
    }
  }

  private rangeLengthRule(rule: ScwMatInputRule): void {
    if (
      this.inputModel &&
      (this.inputModel.length < rule.rangeLength.min || this.inputModel.length > rule.rangeLength.max)
    ) {
      this.showErrorMessage(
        rule.message ??
          ('scwMatForm.validation.rangeLength'),
      );
    }
  }

  private emailRule(rule: ScwMatInputRule): void {
    if (this.inputModel && !isEmail(this.inputModel)) {
      this.showErrorMessage(rule.message ?? ('scwMatForm.validation.email'));
    }
  }

  private patternRule(rule: ScwMatInputRule): void {
    if (this.inputModel && !rule.pattern.test(this.inputModel)) {
      this.showErrorMessage(rule.message);
    }
  }

  private customRule(rule: ScwMatInputRule): void {
    if (this.inputModel && rule.custom && !rule.validator(this.inputModel)) {
      this.showErrorMessage(rule.message);
    }
  }

  private checkRules(): void {
    if (this.rules.length === 0) {
      this.isValidEqualizer(true);
      return;
    }

    this.isAnyError = false;

    for (const rule of this.rules) {
      if (this.isAnyError) {
        return;
      }

      switch (true) {
        case 'required' in rule:
          this.requiredRule(rule);
          break;
        case 'minValue' in rule:
          this.minValueRule(rule);
          break;
        case 'maxValue' in rule:
          this.maxValueRule(rule);
          break;
        case 'minLength' in rule:
          this.minLengthRule(rule);
          break;
        case 'maxLength' in rule:
          this.maxLengthRule(rule);
          break;
        case 'decimal' in rule:
          this.decimalRule(rule);
          break;
        case 'rangeValue' in rule:
          this.rangeValueRule(rule);
          break;
        case 'rangeLength' in rule:
          this.rangeLengthRule(rule);
          break;
        case 'email' in rule:
          this.emailRule(rule);
          break;
        case 'pattern' in rule:
          this.patternRule(rule);
          break;
        case 'custom' in rule:
          this.customRule(rule);
          break;
        default:
          return;
      }
    }

    if (this.isAnyError) {
      return;
    }

    this.clearErrorMessage();
  }

  public onClickEyeIcon(): void {
    this.manageableType = this.manageableType === 'text' ? 'password' : 'text';
  }

  public onNgModelChange(): void {
    this.inputModelChange.emit(this.inputModel);
    this.checkRules();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((item: Subscription) => {
      item.unsubscribe();
    });
  }

  public ngOnInit(): void {
    this.manageableType = this.type;

    this.subscriptions.push(
      this.store.select('user').subscribe((state) => {
        this.decimalSeparator = state.decimalSeparator;
      }),
    );
  }
}
